import { Sides } from "@project/emerge-types";
import { Euler, Quaternion, Vector3 } from "three";

/**
 * Konvertiert eine Seite in einen Normalenvektor
 * @param side Die Seite
 * @returns Normalenvektor der Seite
 */
export function sideToNormalVector(side: Sides): Vector3 {
  switch (side) {
    case Sides.FRONT:
      return new Vector3(0, 0, 1);
    case Sides.BACK:
      return new Vector3(0, 0, -1);
    case Sides.LEFT:
      return new Vector3(-1, 0, 0);
    case Sides.RIGHT:
      return new Vector3(1, 0, 0);
    case Sides.TOP:
      return new Vector3(0, 1, 0);
    case Sides.BOTTOM:
      return new Vector3(0, -1, 0);
    default:
      return new Vector3(0, 0, 1); // Default: FRONT
  }
}

/**
 * Konvertiert eine Seite in einen "Up"-Vektor (senkrecht zur Normalen)
 * @param side Die Seite
 * @returns Up-Vektor des Koordinatensystem des Panels
 */
export function sideToUpVector(side: Sides): Vector3 {
  switch (side) {
    case Sides.FRONT:
      return new Vector3(0, -1, 0); // Y-Achse
    case Sides.BACK:
      return new Vector3(0, -1, 0); // -Y-Achse
    case Sides.LEFT:
    case Sides.RIGHT:
      return new Vector3(0, -1, 0); // -Y-Achse
    case Sides.TOP:
      return new Vector3(0, 0, 1); // Z-Achse
    case Sides.BOTTOM:
      return new Vector3(0, 0, -1); // -Z-Achse
    default:
      return new Vector3(0, 1, 0); // Default: Y-Achse
  }
}

/**
 * Konvertiert einen Normalenvektor zurück in eine Seite
 * @param vector Der Normalenvektor
 * @returns Die entsprechende Seite
 */
export function vectorToSide(vector: Vector3): Sides {
  // Normalisiere den Vektor
  const normalized = vector.clone().normalize();

  // Finde die Komponente mit dem größten absoluten Wert
  const absX = Math.abs(normalized.x);
  const absY = Math.abs(normalized.y);
  const absZ = Math.abs(normalized.z);

  if (absX >= absY && absX >= absZ) {
    return normalized.x > 0 ? Sides.RIGHT : Sides.LEFT;
  } else if (absY >= absX && absY >= absZ) {
    return normalized.y > 0 ? Sides.TOP : Sides.BOTTOM;
  } else {
    return normalized.z > 0 ? Sides.FRONT : Sides.BACK;
  }
}

// Konstanten für die Vektoren (definiert über sideToVector)
const UP = sideToNormalVector(Sides.TOP);
const FRONT = sideToNormalVector(Sides.FRONT);
const RIGHT = sideToNormalVector(Sides.RIGHT);
const LEFT = sideToNormalVector(Sides.LEFT);

/**
 * Rotiert eine Seite mit einer bestimmten Orientierung
 * Implementierung basierend auf AbstractBuildstep.rotateOntoSide
 * @param side Die Seite
 * @param orientation Die Orientierung in Grad
 * @returns Euler-Rotation
 */
export function rotateOntoSide(side: Sides, orientation = 0): Euler {
  // Konvertiere Orientierung von Grad zu Radiant
  let orientRad = (orientation * Math.PI) / 180;

  let sideQuat = new Quaternion();
  switch (side) {
    case Sides.TOP: {
      // Keine zusätzliche Rotation nötig
      sideQuat = new Quaternion();
      break;
    }
    case Sides.BOTTOM: {
      // Rotiere 180° um X-Achse
      sideQuat.setFromAxisAngle(new Vector3(1, 0, 0), Math.PI);
      break;
    }
    case Sides.LEFT: {
      // Rotiere -90° um Z-Achse
      sideQuat.setFromUnitVectors(UP, LEFT);
      orientRad += Math.PI / 2;
      break;
    }
    case Sides.RIGHT: {
      // Rotiere 90° um Z-Achse
      sideQuat.setFromUnitVectors(UP, RIGHT);
      orientRad -= Math.PI / 2;
      break;
    }
    case Sides.FRONT: {
      // Rotiere 90° um X-Achse
      sideQuat.setFromUnitVectors(UP, FRONT);
      break;
    }
    case Sides.BACK: {
      // Rotiere -90° um X-Achse und 180° um Y-Achse
      sideQuat
        .setFromAxisAngle(new Vector3(1, 0, 0), -Math.PI / 2)
        .multiply(
          new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), Math.PI),
        );
      break;
    }
  }

  // Kombiniere die Rotationen und konvertiere zurück zu Euler
  const orientQuat = new Quaternion().setFromAxisAngle(
    new Vector3(0, 1, 0),
    -orientRad,
  );
  return new Euler().setFromQuaternion(sideQuat.multiply(orientQuat));
}
